import { Component, computed, input, OnDestroy, output, signal, viewChild } from '@angular/core';
import { Waiter } from '@gametailors/web-core/misc';
import { cn } from '@gametailors/web-core/tailwind';
import { IconMicrophone } from '../../icons/i-microphone';
import { IconPlay } from '../../icons/i-play';
import { IconStop } from '../../icons/i-stop';
import { AudioRecorder } from '../../utils/audio.utils';
import { RidAudioBubble } from '../rid-audio-bubble/rid-audio-bubble';

@Component({
  selector: 'rid-voice-recorder',
  imports: [RidAudioBubble, IconMicrophone, IconStop, IconPlay],
  templateUrl: './rid-voice-recorder.html',
})
export class RidVoiceRecorder implements OnDestroy {
  size = input<'SMALL' | 'MEDIUM' | 'LARGE'>('MEDIUM');

  audioBubble = viewChild.required<RidAudioBubble>('audioBubble');
  isRecording = signal(false);
  isPlaying = signal(false);
  recording = signal<string | undefined>(undefined);
  hasRecording = computed(() => this.recording() !== undefined);
  twClasses = computed(() =>
    cn(
      'text-white border-red-500 shadow-red-600 hover:border-red-600 hover:shadow-red-700 focus:border-red-600 focus:shadow-red-700 bg-red-500 hover:bg-red-600 focus:bg-red-600',
      {
        LARGE: 'rounded-xl p-8 shadow-[0_0.25em_0] active:translate-y-[0.25em]',
        SMALL: 'rounded-md p-4 shadow-[0_0.125em_0] active:translate-y-[0.125em]',
        MEDIUM: 'rounded-lg p-6 shadow-[0_0.1875em_0] active:translate-y-[0.1875em]',
      }[this.size()],
      this.isRecording() && 'text-red-500 bg-white hover:bg-slate-100 focus:bg-slate-100',
      this.hasRecording() &&
        !this.isPlaying() &&
        'text-white bg-emerald-500 hover:bg-emerald-600 focus:bg-emerald-600 border-emerald-500 shadow-emerald-600 hover:border-emerald-600 hover:shadow-emerald-700 focus:border-emerald-600 focus:shadow-emerald-700',
      this.isPlaying() &&
        'text-white bg-slate-400 border-slate-400 shadow-slate-500 cursor-not-allowed hover:bg-slate-400 hover:border-slate-400 hover:shadow-slate-500 focus:bg-slate-400 focus:border-slate-400 focus:shadow-slate-500',
    ),
  );

  playedBack = output<void>();

  private readonly waiter = new Waiter();

  ngOnDestroy() {
    this.revokeRecordingUrl();
  }

  public async record() {
    try {
      this.isRecording.set(true);

      const recorder = new AudioRecorder();
      await recorder.startRecording();

      const interval = setInterval(() => this.audioBubble().play(), 500);

      await this.waiter.wait();

      clearInterval(interval);
      this.isRecording.set(false);
      this.revokeRecordingUrl();
      this.recording.set(await recorder.stopRecording());
      void this.playback();
    } catch (error) {
      console.error('Error accessing microphone:', error);
      this.isRecording.set(false);
    }
  }

  public stopRecording() {
    this.waiter.finish();
  }

  public async playback() {
    const url = this.recording();
    if (url) {
      this.isPlaying.set(true);
      const audio = new Audio(url);
      const interval = setInterval(() => this.audioBubble().play(), 500);
      const stopPlayback = () => {
        clearInterval(interval);
        this.isPlaying.set(false);
      };

      audio.onended = () => {
        stopPlayback();
        this.playedBack.emit();
      };
      audio.onerror = () => {
        stopPlayback();
      };

      try {
        await audio.play();
      } catch (error) {
        stopPlayback();
        console.error('Error playing recording:', error);
      }
    }
  }

  private revokeRecordingUrl() {
    const url = this.recording();
    if (url) {
      URL.revokeObjectURL(url);
    }
  }
}
